import { useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Download, 
  TrendingUp, 
  DollarSign, 
  Calendar,
  Users,
  Loader2,
  FileText,
  Printer
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function GeneralSeasonsReport() {
  const { t } = useTranslation();
  const [selectedSeason, setSelectedSeason] = useState<string>("all");
  const [selectedGroup, setSelectedGroup] = useState<string>("all");

  // Fetch seasons
  const { data: seasons = [] } = useQuery(["seasons"], async () => {
    const { data } = await api.get("/seasons");
    return data.results || [];
  });

  // Fetch groups
  const { data: groups = [] } = useQuery(["groups"], async () => {
    const { data } = await api.get("/groups");
    return data.results || [];
  });

  // Fetch contributions for analytics
  const contributionsQuery = useQuery({
    queryKey: ["season-contributions", selectedSeason, selectedGroup],
    queryFn: async () => {
      const filters: any[] = [];
      
      if (selectedGroup !== "all") {
        filters.push({
          field: "groupId",
          operator: "eq",
          value: Number(selectedGroup),
        });
      }

      const { data } = await api.get("/contributions", {
        params: {
          page_size: 10000,
          page: 1,
          filters: filters.length > 0 ? filters : undefined,
        },
      });

      return data.results || [];
    },
  });

  // Fetch loans for analytics
  const loansQuery = useQuery({
    queryKey: ["season-loans", selectedSeason, selectedGroup],
    queryFn: async () => {
      const filters: any[] = [];
      
      if (selectedGroup !== "all") {
        filters.push({
          field: "groupId",
          operator: "eq",
          value: Number(selectedGroup),
        });
      }

      const { data } = await api.get("/loans", {
        params: {
          page_size: 10000,
          page: 1,
          filters: filters.length > 0 ? filters : undefined,
        },
      });

      return data.results || [];
    },
  });

  // Fetch expenses for analytics
  const expensesQuery = useQuery({
    queryKey: ["season-expenses", selectedSeason, selectedGroup],
    queryFn: async () => {
      const filters: any[] = [];
      
      if (selectedGroup !== "all") {
        filters.push({
          field: "groupId",
          operator: "eq",
          value: Number(selectedGroup),
        });
      }

      const { data } = await api.get("/expenses", {
        params: {
          page_size: 10000,
          page: 1,
          filters: filters.length > 0 ? filters : undefined,
        },
      });

      return data.results || [];
    },
  });

  const contributions = contributionsQuery.data || [];
  const loans = loansQuery.data || [];
  const expenses = expensesQuery.data || [];

  // Calculate comprehensive statistics
  const stats = {
    // Contributions
    totalContributions: contributions.length,
    totalDeposits: contributions.reduce((sum, c) => sum + (Number(c.depositAmount) || 0), 0),
    totalSolidarity: contributions.reduce((sum, c) => sum + (Number(c.solidarityAmount) || 0), 0),
    
    // Loans
    totalLoans: loans.length,
    totalLoansDisbursed: loans.reduce((sum, l) => sum + (Number(l.amount) || 0), 0),
    totalLoansRepaid: loans.reduce((sum, l) => sum + (Number(l.paidAmount) || 0), 0),
    totalLoansOutstanding: loans.reduce((sum, l) => sum + (Number(l.outstandingBalance) || 0), 0),
    approvedLoans: loans.filter(l => l.status === "APPROVED").length,
    
    // Expenses
    totalExpenses: expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0),
    expenseCount: expenses.length,
    
    // Members
    activeMembers: new Set(contributions.map(c => c.member?.id).filter(Boolean)).size,
    activeBorrowers: new Set(loans.map(l => l.member?.id).filter(Boolean)).size,
    
    // Groups
    activeGroups: selectedGroup === "all" 
      ? new Set([...contributions.map(c => c.group?.id), ...loans.map(l => l.group?.id)].filter(Boolean)).size
      : 1,
  };

  // Round all monetary values
  stats.totalDeposits = Math.round(stats.totalDeposits);
  stats.totalSolidarity = Math.round(stats.totalSolidarity);
  stats.totalLoansDisbursed = Math.round(stats.totalLoansDisbursed);
  stats.totalLoansRepaid = Math.round(stats.totalLoansRepaid);
  stats.totalLoansOutstanding = Math.round(stats.totalLoansOutstanding);
  stats.totalExpenses = Math.round(stats.totalExpenses);

  const totalSavings = stats.totalDeposits + stats.totalSolidarity;
  const netPosition = totalSavings + stats.totalLoansRepaid - stats.totalLoansDisbursed - stats.totalExpenses;

  // Group-wise breakdown
  const groupBreakdown = groups.map(group => {
    const groupContributions = contributions.filter(c => c.group?.id === group.id);
    const groupLoans = loans.filter(l => l.group?.id === group.id);
    const groupExpenses = expenses.filter(e => e.group?.id === group.id);
    
    const totalDeposits = groupContributions.reduce((sum, c) => sum + (Number(c.depositAmount) || 0), 0);
    const totalSolidarity = groupContributions.reduce((sum, c) => sum + (Number(c.solidarityAmount) || 0), 0);
    const loansDisbursed = groupLoans.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
    const loansRepaid = groupLoans.reduce((sum, l) => sum + (Number(l.paidAmount) || 0), 0);
    const totalExpenses = groupExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    
    return {
      id: group.id,
      name: group.name,
      branch: group.branch?.name || "—",
      district: group.branch?.district?.name || "—",
      members: groupContributions.length > 0 ? new Set(groupContributions.map(c => c.member?.id).filter(Boolean)).size : 0,
      totalSavings: totalDeposits + totalSolidarity,
      loansDisbursed,
      loansRepaid,
      expenses: totalExpenses,
      netPosition: (totalDeposits + totalSolidarity + loansRepaid) - loansDisbursed - totalExpenses,
    };
  }).filter(g => selectedGroup === "all" ? true : g.id.toString() === selectedGroup);

  // Export to Excel
  const exportToExcel = () => {
    try {
      if (groupBreakdown.length === 0) {
        toast.error("No data to export");
        return;
      }

      const data = groupBreakdown.map((g) => ({
        Group: g.name,
        Branch: g.branch,
        District: g.district,
        "Active Members": g.members,
        "Total Savings": Math.round(g.totalSavings),
        "Loans Disbursed": Math.round(g.loansDisbursed),
        "Loans Repaid": Math.round(g.loansRepaid),
        Expenses: Math.round(g.expenses),
        "Net Position": Math.round(g.netPosition),
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Group Breakdown");

      const filename = `general-seasons-report-${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(workbook, filename);
      toast.success("Excel file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export Excel: " + (error.message || "Unknown error"));
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      if (groupBreakdown.length === 0) {
        toast.error("No data to export");
        return;
      }

      const doc = new jsPDF('landscape');
      const seasonName = selectedSeason !== "all" 
        ? seasons.find(s => s.id.toString() === selectedSeason)?.name || "All Seasons"
        : "All Seasons";
      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      // Header
      doc.setFontSize(16);
      doc.text("General Seasons Report", 14, 10);
      doc.setFontSize(12);
      doc.text(`Season: ${seasonName}`, 14, 18);
      doc.text(`Group: ${groupName}`, 14, 24);
      doc.text(`Generated: ${format(new Date(), "MMM dd, yyyy")}`, 14, 30);

      // Summary Statistics
      doc.setFontSize(10);
      let yPos = 38;
      doc.text(`Total Savings: ${totalSavings.toLocaleString()} RWF`, 14, yPos);
      yPos += 6;
      doc.text(`Loans Disbursed: ${stats.totalLoansDisbursed.toLocaleString()} RWF`, 14, yPos);
      yPos += 6;
      doc.text(`Loans Repaid: ${stats.totalLoansRepaid.toLocaleString()} RWF`, 14, yPos);
      yPos += 6;
      doc.text(`Total Expenses: ${stats.totalExpenses.toLocaleString()} RWF`, 14, yPos);
      yPos += 6;
      doc.text(`Net Position: ${netPosition.toLocaleString()} RWF`, 14, yPos);
      yPos += 6;
      doc.text(`Active Members: ${stats.activeMembers}`, 14, yPos);
      yPos += 6;
      doc.text(`Active Borrowers: ${stats.activeBorrowers}`, 14, yPos);
      yPos += 6;
      doc.text(`Active Groups: ${stats.activeGroups}`, 14, yPos);

      // Prepare table data
      const tableData = groupBreakdown.map((g) => [
        g.name,
        g.branch,
        g.district,
        g.members.toString(),
        Math.round(g.totalSavings).toLocaleString(),
        Math.round(g.loansDisbursed).toLocaleString(),
        Math.round(g.loansRepaid).toLocaleString(),
        Math.round(g.expenses).toLocaleString(),
        Math.round(g.netPosition).toLocaleString(),
      ]);

      const headers = [
        "Group",
        "Branch",
        "District",
        "Members",
        "Total Savings",
        "Loans Disbursed",
        "Loans Repaid",
        "Expenses",
        "Net Position",
      ];

      // Add table
      autoTable(doc as any, {
        head: [headers],
        body: tableData,
        startY: yPos + 5,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [249, 250, 251] },
        margin: { left: 14, right: 14 },
      });

      const filename = `general-seasons-report-${format(new Date(), "yyyy-MM-dd")}.pdf`;
      doc.save(filename);
      toast.success("PDF file exported successfully");
    } catch (error: any) {
      toast.error("Failed to export PDF: " + (error.message || "Unknown error"));
    }
  };

  // Print report
  const handlePrint = () => {
    try {
      if (groupBreakdown.length === 0) {
        toast.error("No data to print");
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Please allow popups to print the report");
        return;
      }

      const seasonName = selectedSeason !== "all" 
        ? seasons.find(s => s.id.toString() === selectedSeason)?.name || "All Seasons"
        : "All Seasons";
      const groupName = selectedGroup !== "all" 
        ? groups.find(g => g.id.toString() === selectedGroup)?.name || "All Groups"
        : "All Groups";

      const generatedDate = format(new Date(), "MMM dd, yyyy");
      const generatedTime = format(new Date(), "HH:mm:ss");
      
      const tableRows = groupBreakdown.map((g) => {
        return "<tr>" +
          "<td>" + g.name + "</td>" +
          "<td>" + g.branch + "</td>" +
          "<td>" + g.district + "</td>" +
          "<td class=\"text-right\">" + g.members + "</td>" +
          "<td class=\"text-right\">" + Math.round(g.totalSavings).toLocaleString() + "</td>" +
          "<td class=\"text-right\">" + Math.round(g.loansDisbursed).toLocaleString() + "</td>" +
          "<td class=\"text-right text-green-600\">" + Math.round(g.loansRepaid).toLocaleString() + "</td>" +
          "<td class=\"text-right text-orange-600\">" + Math.round(g.expenses).toLocaleString() + "</td>" +
          "<td class=\"text-right " + (g.netPosition >= 0 ? "text-green-600" : "text-red-600") + "\">" + Math.round(g.netPosition).toLocaleString() + "</td>" +
          "</tr>";
      }).join('');

      const htmlContent = "<!DOCTYPE html>" +
        "<html>" +
        "<head>" +
        "<title>General Seasons Report</title>" +
        "<style>" +
        "@media print { @page { size: landscape; margin: 1cm; } body { margin: 0; padding: 0; } }" +
        "body { font-family: Arial, sans-serif; padding: 20px; }" +
        ".header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }" +
        ".header h1 { margin: 0; font-size: 24px; color: #333; }" +
        ".header h2 { margin: 5px 0; font-size: 18px; color: #666; }" +
        ".summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin: 20px 0; }" +
        ".summary-item { padding: 15px; background-color: #f5f5f5; border-radius: 5px; text-align: center; }" +
        ".summary-item strong { display: block; font-size: 18px; color: #333; }" +
        ".summary-item span { font-size: 14px; color: #666; }" +
        "table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 10px; }" +
        "th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }" +
        "th { background-color: #3b82f6; color: white; font-weight: bold; text-align: center; }" +
        "tr:nth-child(even) { background-color: #f9fafb; }" +
        ".text-right { text-align: right; }" +
        ".text-green-600 { color: #16a34a; }" +
        ".text-red-600 { color: #dc2626; }" +
        ".text-orange-600 { color: #ea580c; }" +
        ".footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }" +
        "</style>" +
        "</head>" +
        "<body>" +
        "<div class=\"header\">" +
        "<h1>General Seasons Report</h1>" +
        "<h2>Season: " + seasonName + " | Group: " + groupName + "</h2>" +
        "<p>Generated: " + generatedDate + "</p>" +
        "</div>" +
        "<div class=\"summary\">" +
        "<div class=\"summary-item\"><strong>" + totalSavings.toLocaleString() + " RWF</strong><span>Total Savings</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalLoansDisbursed.toLocaleString() + " RWF</strong><span>Loans Disbursed</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalLoansRepaid.toLocaleString() + " RWF</strong><span>Loans Repaid</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.totalExpenses.toLocaleString() + " RWF</strong><span>Total Expenses</span></div>" +
        "<div class=\"summary-item\"><strong>" + netPosition.toLocaleString() + " RWF</strong><span>Net Position</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.activeMembers + "</strong><span>Active Members</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.activeBorrowers + "</strong><span>Active Borrowers</span></div>" +
        "<div class=\"summary-item\"><strong>" + stats.activeGroups + "</strong><span>Active Groups</span></div>" +
        "</div>" +
        "<table>" +
        "<thead>" +
        "<tr><th>Group</th><th>Branch</th><th>District</th><th class=\"text-right\">Members</th><th class=\"text-right\">Total Savings</th><th class=\"text-right\">Loans Disbursed</th><th class=\"text-right\">Loans Repaid</th><th class=\"text-right\">Expenses</th><th class=\"text-right\">Net Position</th></tr>" +
        "</thead>" +
        "<tbody>" + tableRows + "</tbody>" +
        "</table>" +
        "<div class=\"footer\">" +
        "<p>General Seasons Report generated on " + generatedDate + " at " + generatedTime + "</p>" +
        "</div>" +
        "</body>" +
        "</html>";

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          toast.success("Print dialog opened");
        }, 250);
      };
    } catch (error: any) {
      toast.error("Failed to print: " + (error.message || "Unknown error"));
    }
  };

  const clearFilters = () => {
    setSelectedSeason("all");
    setSelectedGroup("all");
  };

  const isLoading = contributionsQuery.isLoading || loansQuery.isLoading || expensesQuery.isLoading;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t("pages.generalSeasonsReport.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("pages.generalSeasonsReport.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportToPDF}
            disabled={groupBreakdown.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.pdf")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportToExcel}
            disabled={groupBreakdown.length === 0}
          >
            <Download className="h-4 w-4 mr-1" />
            {t("common.excel")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={groupBreakdown.length === 0}
          >
            <Printer className="h-4 w-4 mr-1" />
            {t("common.print")}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("pages.generalSeasonsReport.filters.title")}</CardTitle>
          <CardDescription>{t("pages.generalSeasonsReport.filters.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.generalSeasonsReport.filters.savingSeason")}</label>
              <Select value={selectedSeason} onValueChange={setSelectedSeason}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.generalSeasonsReport.filters.allSeasons")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.generalSeasonsReport.filters.allSeasons")}</SelectItem>
                  {seasons.map((s) => (
                    <SelectItem key={s.id} value={s.id.toString()}>
                      {s.name} ({s.startDate ? format(new Date(s.startDate), "MMM yyyy") : ""} - {s.endDate ? format(new Date(s.endDate), "MMM yyyy") : ""})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.generalSeasonsReport.filters.group")}</label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder={t("pages.generalSeasonsReport.filters.allGroups")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("pages.generalSeasonsReport.filters.allGroups")}</SelectItem>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id.toString()}>
                      {g.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(selectedSeason !== "all" || selectedGroup !== "all") && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={clearFilters}>
                {t("pages.generalSeasonsReport.filters.clearFilters")}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.totalSavings")}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalSavings.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalContributions === 1 
                ? t("pages.generalSeasonsReport.stats.contribution", { count: stats.totalContributions })
                : t("pages.generalSeasonsReport.stats.contributions", { count: stats.totalContributions })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.loansDisbursed")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalLoansDisbursed.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.totalLoans === 1 
                ? t("pages.generalSeasonsReport.stats.loan", { count: stats.totalLoans })
                : t("pages.generalSeasonsReport.stats.loans", { count: stats.totalLoans })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.totalExpenses")}</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalExpenses.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.expenseCount === 1 
                ? t("pages.generalSeasonsReport.stats.expense", { count: stats.expenseCount })
                : t("pages.generalSeasonsReport.stats.expenses", { count: stats.expenseCount })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.netPosition")}</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${netPosition >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {netPosition.toLocaleString()} RWF
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {netPosition >= 0 ? t("pages.generalSeasonsReport.stats.surplus") : t("pages.generalSeasonsReport.stats.deficit")}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.activeMembers")}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeMembers}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.generalSeasonsReport.stats.contributingMembers")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.activeBorrowers")}</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeBorrowers}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.generalSeasonsReport.stats.membersWithLoans")}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.loansRepaid")}</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.totalLoansRepaid.toLocaleString()} RWF
            </div>
            <p className="text-xs text-green-600 mt-1">
              {t("pages.generalSeasonsReport.stats.repaymentRate", { 
                rate: stats.totalLoansDisbursed > 0 
                ? ((stats.totalLoansRepaid / stats.totalLoansDisbursed) * 100).toFixed(1)
                  : "0"
              })}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("pages.generalSeasonsReport.stats.activeGroups")}</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeGroups}</div>
            <p className="text-xs text-muted-foreground mt-1">{t("pages.generalSeasonsReport.stats.participatingGroups")}</p>
          </CardContent>
        </Card>
      </div>

      {/* Group Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("pages.generalSeasonsReport.table.title")}</CardTitle>
          <CardDescription>
            {t("pages.generalSeasonsReport.table.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : groupBreakdown.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">{t("common.noData")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("pages.generalSeasonsReport.table.group")}</TableHead>
                    <TableHead>{t("pages.generalSeasonsReport.table.branch")}</TableHead>
                    <TableHead>{t("pages.generalSeasonsReport.table.district")}</TableHead>
                    <TableHead className="text-right">{t("pages.generalSeasonsReport.table.members")}</TableHead>
                    <TableHead className="text-right">{t("pages.generalSeasonsReport.table.totalSavings")}</TableHead>
                    <TableHead className="text-right">{t("pages.generalSeasonsReport.table.loansDisbursed")}</TableHead>
                    <TableHead className="text-right">{t("pages.generalSeasonsReport.table.loansRepaid")}</TableHead>
                    <TableHead className="text-right">{t("pages.generalSeasonsReport.table.expenses")}</TableHead>
                    <TableHead className="text-right">{t("pages.generalSeasonsReport.table.netPosition")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupBreakdown.map((group) => (
                    <TableRow key={group.id}>
                      <TableCell className="font-medium">{group.name}</TableCell>
                      <TableCell>{group.branch}</TableCell>
                      <TableCell>{group.district}</TableCell>
                      <TableCell className="text-right">{group.members}</TableCell>
                      <TableCell className="text-right">
                        {Math.round(group.totalSavings).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {Math.round(group.loansDisbursed).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-green-600">
                        {Math.round(group.loansRepaid).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-orange-600">
                        {Math.round(group.expenses).toLocaleString()}
                      </TableCell>
                      <TableCell className={`text-right font-medium ${group.netPosition >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {Math.round(group.netPosition).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
